import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const googleReady = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Called by Google's own script the moment someone completes sign-in —
  // sends the token straight to our backend, which verifies it directly
  // against Google (no third-party relay involved).
  const handleCredentialResponse = useCallback(
    async (response) => {
      try {
        const { data } = await api.post("/auth/google", { credential: response.credential });
        setUser(data);
        navigate(data.profile_completed ? "/circle" : "/complete-profile", {
          replace: true,
          state: { user: data },
        });
      } catch (err) {
        console.warn("Google sign-in failed", err);
      }
    },
    [navigate]
  );

  // Load Google's script once and register our callback.
  useEffect(() => {
    if (googleReady.current) return;
    const init = () => {
      if (!window.google?.accounts?.id || !GOOGLE_CLIENT_ID) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
      });
      googleReady.current = true;
    };
    if (window.google?.accounts?.id) {
      init();
      return;
    }
    const existing = document.querySelector(`script[src="${GOOGLE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", init);
      return () => existing.removeEventListener("load", init);
    }
    const script = document.createElement("script");
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = init;
    document.body.appendChild(script);
  }, [handleCredentialResponse]);

  // Opens Google's sign-in popup. Any button anywhere can just call login().
  const login = useCallback(() => {
    if (!window.google?.accounts?.id) {
      console.warn("Google Sign-In is still loading — try again in a moment.");
      return;
    }
    window.google.accounts.id.prompt();
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout API call failed (session likely already gone)", err);
    }
    setUser(null);
    sessionStorage.removeItem("circle_geo_ok");
  }, []);

  // Memoized so consumers don't re-render on every provider render
  const value = useMemo(
    () => ({ user, setUser, loading, login, logout }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
